import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function RapportsConsommation() {
    const [periode, setPeriode] = useState('jour');
    const [consommationProduits, setConsommationProduits] = useState([]);
    const [nbEtudiants, setNbEtudiants] = useState(0);
    const [totalConsomme, setTotalConsomme] = useState(0);
    const [topProduits, setTopProduits] = useState([]);
    const [loading, setLoading] = useState(false);

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    const fetchRapports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/financier/get_rapports_consommation.php?periode=${periode}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            console.log('Rapports reçus:', data);
            
            if (data.success) {
                setConsommationProduits(data.consommation_produits || []);
                setNbEtudiants(data.nb_etudiants || 0);
                setTotalConsomme(data.total_consomme || 0);
                setTopProduits(data.top_produits || []);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRapports();
    }, [periode]);

    // Calcul pour les barres
    const maxConsommation = Math.max(...consommationProduits.map(p => p.total_consomme), 1);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">📊 Rapports de consommation</h1>
                    <p className="mt-2 opacity-90">
                        Suivi des produits consommés par l'infirmerie
                    </p>
                </div>

                {/* Filtres période */}
                <div className="mb-6 flex flex-wrap gap-3">
                    <button
                        onClick={() => setPeriode('jour')}
                        className={`px-6 py-2 rounded-xl font-semibold transition ${
                            periode === 'jour'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        📅 Aujourd'hui
                    </button>
                    <button
                        onClick={() => setPeriode('mois')}
                        className={`px-6 py-2 rounded-xl font-semibold transition ${
                            periode === 'mois'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        📆 Ce mois
                    </button>
                    <button
                        onClick={() => setPeriode('annee')}
                        className={`px-6 py-2 rounded-xl font-semibold transition ${
                            periode === 'annee'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        📈 Cette année
                    </button>
                </div>

                {/* Statistiques générales */}
                <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="rounded-2xl bg-white p-5 shadow-lg text-center">
                        <p className="text-sm text-slate-500">Étudiants traités</p>
                        <p className="text-3xl font-bold text-emerald-600">{nbEtudiants}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-5 shadow-lg text-center">
                        <p className="text-sm text-slate-500">Produits consommés</p>
                        <p className="text-3xl font-bold text-blue-600">{totalConsomme}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-5 shadow-lg text-center">
                        <p className="text-sm text-slate-500">Top produits</p>
                        <p className="text-3xl font-bold text-purple-600">{topProduits.length}</p>
                    </div>
                </div>

                {/* Graphique à barres */}
                <div className="mb-6 rounded-2xl bg-white p-6 shadow-lg">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">📊 Consommation par produit</h2>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
                        </div>
                    ) : consommationProduits.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-slate-500">Aucune consommation enregistrée pour cette période</p>
                            <p className="text-sm text-slate-400 mt-2">Les consommations des produits par l'infirmier apparaîtront ici</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {consommationProduits.map((p, idx) => (
                                <div key={idx}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium">{p.nom}</span>
                                        <span className="text-slate-500">{p.total_consomme} unités</span>
                                    </div>
                                    <div className="h-8 w-full rounded-full bg-slate-100 overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-end pr-3 text-white text-xs font-bold"
                                            style={{ width: `${(p.total_consomme / maxConsommation) * 100}%` }}
                                        >
                                            {p.total_consomme > 0 && p.total_consomme}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Top 5 produits */}
                <div className="rounded-2xl bg-white p-6 shadow-lg">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">🏆 Top 5 produits les plus utilisés</h2>
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
                        </div>
                    ) : topProduits.length === 0 ? (
                        <p className="text-center text-slate-500 py-4">Aucune donnée disponible</p>
                    ) : (
                        <div className="space-y-2">
                            {topProduits.map((p, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold">
                                            {idx + 1}
                                        </div>
                                        <span className="font-medium">{p.nom}</span>
                                    </div>
                                    <span className="text-emerald-600 font-bold">{p.total} unités</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}